/**
 * caseCache.js — كاش بَسيط جِدّاً في sessionStorage لِتَفاصيل القَضايا وَمَراحِلها.
 *
 * الفِكرَة (Stale-While-Revalidate):
 *   عِندَما يَفتَح المُستَخدِم قَضيّة، نَحفَظ تَفاصيلها + مَراحِلها مَحَلّيّاً.
 *   عِند العَودَة بَعد إِضافَة مَرحَلَة، يَستَطيع UI عَرض القَضيّة فَوريّاً مِن
 *   الكاش بَدَلاً مِن اِنتِظار شَبَكَتَين مُتَسَلسِلَتَين (getCase ثُمَّ listStages).
 *   ثُمَّ يَجري تَحديث صامِت في الخَلفيّة — لَو تَغَيَّر شَيء، يَنعَكِس بِدون
 *   كَسر التَّجرِبَة.
 *
 * الحَياة: 10 دَقائِق — كافيَة لِسيناريو "اِفتَح قَضيّة → أَضِف مَرحَلَة → عُد"
 *   لٰكِن لَيست طَويلَة بِما يَكفي لِعَرض بَيانات قَديمَة جِدّاً.
 *
 * النِّطاق: sessionStorage — يُمحَى تِلقائيّاً عِند إغلاق التَّبويب. لا نُريد
 *   بَيانات قَضايا في localStorage الدّائِم (تَسريب خُصوصيّة لَو شارَك الجِهاز).
 */
const CASE_KEY = (id) => `lex.cache.case.v1.${id}`;
const STAGES_KEY = (id) => `lex.cache.stages.v1.${id}`;
const TTL_MS = 10 * 60 * 1000;

function safeGet(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { data, at } = JSON.parse(raw);
    if (typeof at !== "number" || Date.now() - at > TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function safeSet(key, data) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, at: Date.now() }));
  } catch {
    /* مَوضوع غير حَرِج — اِستِمِرّ بِدون كاش */
  }
}

function safeRemove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* no-op */
  }
}

export const getCachedCase = (id) => (id ? safeGet(CASE_KEY(id)) : null);
export const setCachedCase = (id, data) => id && safeSet(CASE_KEY(id), data);

export const getCachedStages = (caseId) =>
  caseId ? safeGet(STAGES_KEY(caseId)) : null;
export const setCachedStages = (caseId, stages) =>
  caseId && safeSet(STAGES_KEY(caseId), stages);

/* عِندَ إضافَة مَرحَلَة جَديدة، أَدخِلها في الكاش لِتَظهَر فَوريّاً
   عِند العَودَة لِلتَّايملاين. التَّرتيب الزَّمَنيّ يُحَدَّد لاحِقاً مِن
   تَحديث الشَّبَكَة. */
export function prependStageToCache(caseId, stage) {
  if (!caseId || !stage) return;
  const list = getCachedStages(caseId) || [];
  const next = [stage, ...list.filter((s) => s.id !== stage.id)];
  setCachedStages(caseId, next);
}

/* عِندَ تَعديل مَرحَلَة، اِستَبدِلها في الكاش. */
export function replaceStageInCache(caseId, stage) {
  if (!caseId || !stage?.id) return;
  const list = getCachedStages(caseId);
  if (!list) return;
  const next = list.map((s) => (s.id === stage.id ? { ...s, ...stage } : s));
  setCachedStages(caseId, next);
}

/* عِندَ حَذف مَرحَلَة، أَزِلها مِنَ الكاش. */
export function removeStageFromCache(caseId, stageId) {
  if (!caseId || !stageId) return;
  const list = getCachedStages(caseId);
  if (!list) return;
  setCachedStages(caseId, list.filter((s) => s.id !== stageId));
}

export const clearCaseCache = (id) => {
  if (!id) return;
  safeRemove(CASE_KEY(id));
  safeRemove(STAGES_KEY(id));
};
